import { useMutation, useQueryClient } from "react-query";

import { updateTargetMarker } from "api/targets";
import { useGlobalToast } from "components/GlobalToastProvider";
import type { UpdateTargetMarkerBody } from "api/types/markers";
import { invalidateTargetMarkers } from "./invalidateTargetMarkers";

interface UpdateTargetMarkerPayload {
  targetId: number;
  markerId: number;
  data: UpdateTargetMarkerBody;
  /**
   * Targets the marker applied to before the edit. Their charts are refreshed
   * too, so a target removed from the marker stops showing it.
   */
  affectedTargetIds?: number[];
}

/**
 * Updates a chart marker's editable fields (`label`, `notes`, `transition_to`,
 * `marker_date` / `target_session_id`, `target_ids`). `marker_type` is immutable
 * server-side. On success invalidates the markers query of every target the
 * marker applied to, before and after.
 */
const useUpdateTargetMarker = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: updateMarkerAction,
    isLoading: isUpdatingMarker,
    ...rest
  } = useMutation(
    ({ targetId, markerId, data }: UpdateTargetMarkerPayload) =>
      updateTargetMarker(targetId, markerId, data),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Marker updated" });
        invalidateTargetMarkers(queryClient, [
          variables.targetId,
          ...(variables.affectedTargetIds ?? []),
          ...(response?.data?.target_ids ?? variables.data.target_ids ?? []),
        ]);
        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || "Failed to update marker";
        showToast({ message: errorMessage, errorState: true });
        onError(error);
      },
      ...otherOptions,
    }
  );

  return { updateMarkerAction, isUpdatingMarker, ...rest };
};

export default useUpdateTargetMarker;
